import { request } from "$/lib/api"
import {
  CreateStorageLocation,
  DefaultStorageLocationDto,
  Route,
  SetDefaultStorageLocation,
  StorageLocationDto,
  UpdateStorageLocation,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

const fetchList = (storeId: string): Promise<StorageLocationDto[]> =>
  request<StorageLocationDto[]>({
    url: populateRoute(
      Route.STORAGE_LOCATION_CONTROLLER__FIND_MANY_FOR_STORE,
      storeId,
    ),
    method: "GET",
  })

const createStorageLocation = (
  storeId: string,
  data: CreateStorageLocation,
): Promise<StorageLocationDto> =>
  request<StorageLocationDto>({
    url: populateRoute(Route.STORAGE_LOCATION_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })

const updateStorageLocation = (
  storageLocationId: string,
  data: UpdateStorageLocation,
): Promise<StorageLocationDto> =>
  request<StorageLocationDto>({
    url: populateRoute(
      Route.STORAGE_LOCATION_CONTROLLER__UPDATE,
      storageLocationId,
    ),
    method: "PUT",
    data,
  })

const deleteStorageLocation = (storageLocationId: string): Promise<void> =>
  request<void>({
    url: populateRoute(
      Route.STORAGE_LOCATION_CONTROLLER__DELETE,
      storageLocationId,
    ),
    method: "DELETE",
  })

const fetchDefault = (storeId: string): Promise<DefaultStorageLocationDto> =>
  request<DefaultStorageLocationDto>({
    url: populateRoute(Route.STORAGE_LOCATION_CONTROLLER__GET_DEFAULT, storeId),
    method: "GET",
  })

const setDefaultStorageLocation = (
  storeId: string,
  data: SetDefaultStorageLocation,
): Promise<DefaultStorageLocationDto> =>
  request<DefaultStorageLocationDto>({
    url: populateRoute(Route.STORAGE_LOCATION_CONTROLLER__SET_DEFAULT, storeId),
    method: "PUT",
    data,
  })

const storageLocationQueries = {
  all: () => ["storageLocation"],
  lists: () => [...storageLocationQueries.all(), "list"],
  list: (storeId: string) =>
    queryOptions({
      queryKey: [...storageLocationQueries.lists(), storeId],
      queryFn: () => fetchList(storeId),
    }),
  defaultLocation: (storeId: string) =>
    queryOptions({
      queryKey: [...storageLocationQueries.all(), "default", storeId],
      queryFn: () => fetchDefault(storeId),
    }),
}

export function useStorageLocationMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: storageLocationQueries.all(),
    })
  }

  const saveMutation = useMutation({
    mutationFn: (data: CreateStorageLocation) =>
      createStorageLocation(storeId, data),
    onSettled: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      storageLocationId,
      data,
    }: { storageLocationId: string; data: UpdateStorageLocation }) =>
      updateStorageLocation(storageLocationId, data),
    onSettled: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (storageLocationId: string) =>
      deleteStorageLocation(storageLocationId),
    onSettled: invalidate,
  })

  const setDefaultMutation = useMutation({
    mutationFn: (storageLocationId: string | null) =>
      setDefaultStorageLocation(storeId, { storageLocationId }),
    onSettled: invalidate,
  })

  return { saveMutation, updateMutation, deleteMutation, setDefaultMutation }
}

export default storageLocationQueries
